import classNames from 'classnames/bind';
import { graphql, useStaticQuery } from 'gatsby';
import GatsbyImage from 'gatsby-image';
import debounce from 'lodash/debounce';
import React from 'react';
import { useInView } from 'react-intersection-observer';

import Heading from 'components/shared/heading';
import { BREAKPOINT_SM } from 'constants/breakpoints';
import useLottie from 'hooks/use-lottie';
import useSectionOffset from 'hooks/use-section-offset';
import useWindowWidth from 'hooks/use-window-width';

import animationData from './animation/kros_walk';
import styles from './start-here.module.scss';

const cx = classNames.bind(styles);

const ANIMATION_DURATION_FRAMES = 50;
const ANIMATION_DURATION_SECONDS = 2;
const ANIMATION_SPEED_FACTOR = 0.6;

const StartHere = () => {
  const { stickerHotDeal, stickerPrice } = useStaticQuery(IMAGE_QUERY);

  const [introAnimationStart, setIntroAnimationStart] = React.useState(false);

  const [lottieLoaded, setLottieLoaded] = React.useState(false);
  const [sectionRef, inView] = useInView({});
  const outerSectionRef = React.useRef(null);

  const defaultOptions = {
    loop: false,
    autoplay: false,
    renderer: 'svg',
    animationData,
  };

  const [lottieAnimation, lottieAnimationRef] = useLottie(defaultOptions, {
    loaded_images() {
      this.setSpeed(ANIMATION_SPEED_FACTOR);
      setLottieLoaded(true);
    },
    complete() {
      this.loop = true;
      this.playSegments([0, ANIMATION_DURATION_FRAMES], true);
    },
  });

  // Start the animation once all images and lottie animation will be loaded
  React.useEffect(() => {
    if (lottieLoaded) {
      setIntroAnimationStart(true);
    }
  }, [lottieLoaded]);

  // Start the animation again after it finishes
  React.useEffect(() => {
    if (introAnimationStart) {
      setTimeout(() => {
        lottieAnimation.play();
      }, (ANIMATION_DURATION_SECONDS / ANIMATION_SPEED_FACTOR) * 1000);
    }
  }, [introAnimationStart, lottieAnimation]);

  // Play lottie animation only when section is in viewport
  React.useEffect(() => {
    if (!lottieAnimation) {
      return;
    }
    if (inView && introAnimationStart) {
      lottieAnimation.play();
    } else {
      lottieAnimation.pause();
    }
  }, [inView, lottieAnimation, introAnimationStart]);

  const [isTextAnimated, setIsTextAnimated] = React.useState(false);

  const [sectionStart, sectionEnd, sectionHidden] = useSectionOffset(outerSectionRef);

  // start animating text when bottom of the section is visible, stop when section is out of view
  React.useEffect(() => {
    const scrollHandler = debounce((evt) => {
      if (window.scrollY >= sectionStart && window.scrollY <= sectionHidden) {
        setIsTextAnimated(true);
      } else {
        setIsTextAnimated(false);
      }
    }, 30);

    if (inView) {
      document.addEventListener('scroll', scrollHandler);
    } else {
      document.removeEventListener('scroll', scrollHandler);
      setIsTextAnimated(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView]);

  const windowWidth = useWindowWidth();

  const Title = () => (
    <span className={cx('title-content')}>
      Why go{' '}
      <span className={cx('elsewhere')}>
        elsewhere
        <div className={cx('sticker-price')}>
          <GatsbyImage fluid={stickerPrice.childImageSharp.fluid} fadeIn={false} />
        </div>
      </span>
      <br />
      when you
      <br />
      could
      <br />
      <span className={cx('start-here', isTextAnimated && 'title-animated')}>
        start here
        <div className={cx('sticker-hot-deal')}>
          <GatsbyImage fluid={stickerHotDeal.childImageSharp.fluid} fadeIn={false} />
        </div>
      </span>
    </span>
  );

  const TitleMobile = () => (
    <>
      <span className={cx('title-content')}>
        <span>Why&nbsp;go</span>
        <br />
        <span className={cx('elsewhere')}>
          elsew&#8209;
          <br />
          here
        </span>
        <br />
        when
        <br />
        you
        <br />
        could
        <br />
        <span className={cx('start-here', isTextAnimated && 'title-animated')}>
          start
          <br />
          here
        </span>
      </span>
      <div className={cx('sticker-price')}>
        <GatsbyImage fluid={stickerPrice.childImageSharp.fluid} fadeIn={false} />
      </div>
      <div className={cx('sticker-hot-deal')}>
        <GatsbyImage fluid={stickerHotDeal.childImageSharp.fluid} fadeIn={false} />
      </div>
    </>
  );

  return (
    <section ref={outerSectionRef}>
      <div ref={sectionRef} className={cx('container', 'wrapper')}>
        <Heading className={cx('title')} tag="h2" size="xl" theme="secondary-inverted">
          <div className={cx('scene')}>
            <div className={cx('lottie')} ref={lottieAnimationRef} />
          </div>
          {windowWidth > BREAKPOINT_SM ? <Title /> : <TitleMobile />}
        </Heading>
        <p className={cx('description')}>
          We provide products and tools for beginners and experts alike. Scale
          {windowWidth > BREAKPOINT_SM ? <br /> : <> </>}
          from 0-1, or scale your business from thousands to millions
        </p>
      </div>
    </section>
  );
};

export default StartHere;

const IMAGE_QUERY = graphql`
  query images {
    stickerHotDeal: file(relativePath: { eq: "pages/home/start-here/sticker-hot-deal.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
    stickerPrice: file(relativePath: { eq: "pages/home/start-here/sticker-price.png" }) {
      childImageSharp {
        fluid {
          ...GatsbyImageSharpFluid_withWebp_noBase64
        }
      }
    }
  }
`;
